import { apiClient } from '@/lib/apiClient';
import type {
  PaginadoResponse,
  Banco, SalvarBancoRequest, BancoCombo,
  ContaCorrente, SalvarContaCorrenteRequest, ContaCorrenteCombo,
  Cartao, SalvarCartaoRequest, CartaoCombo,
  FormaPagamento, SalvarFormaPagamentoRequest, FormaPagamentoCombo,
  CondicaoPagamento, CondicaoPagamentoDetalhe, SalvarCondicaoPagamentoRequest, CondicaoPagamentoCombo,
} from '../types';

export interface CentroCusto {
  id: number;
  descricao: string;
  contaContabilId: number;
}

export interface GrupoDespesas {
  id: number;
  descricao: string;
}

export interface TipoDespesas {
  id: number;
  descricao: string;
  grupoDespesasId: number | null;
  centroCustoId: number | null;
  contaContabilId: number | null;
}

export const cadastrosFinanceirosService = {
  // ── Combos auxiliares ────────────────────────────────────────────────────
  async getCentrosCusto(): Promise<CentroCusto[]> {
    const { data } = await apiClient.get<CentroCusto[]>('/api/centros-custo');
    return data;
  },

  async getGruposDespesas(): Promise<GrupoDespesas[]> {
    const { data } = await apiClient.get<GrupoDespesas[]>('/api/grupos-despesas');
    return data;
  },

  async getTiposDespesas(grupoDespesasId?: number): Promise<TipoDespesas[]> {
    const { data } = await apiClient.get<TipoDespesas[]>('/api/tipos-despesas', {
      params: grupoDespesasId ? { grupoDespesasId } : undefined,
    });
    return data;
  },

  // ── Bancos ───────────────────────────────────────────────────────────────
  async getBancosCombo(): Promise<BancoCombo[]> {
    const { data } = await apiClient.get<BancoCombo[]>('/api/bancos');
    return data;
  },

  async listarBancos(params?: { busca?: string; pagina?: number; tamanhoPagina?: number }): Promise<PaginadoResponse<Banco>> {
    const { data } = await apiClient.get<PaginadoResponse<Banco>>('/api/cadastros/bancos', { params });
    return data;
  },

  async criarBanco(req: SalvarBancoRequest): Promise<Banco> {
    const { data } = await apiClient.post<Banco>('/api/cadastros/bancos', req);
    return data;
  },

  async atualizarBanco(id: number, req: SalvarBancoRequest): Promise<void> {
    await apiClient.put(`/api/cadastros/bancos/${id}`, req);
  },

  async excluirBanco(id: number): Promise<void> {
    await apiClient.delete(`/api/cadastros/bancos/${id}`);
  },

  // ── Contas Correntes ─────────────────────────────────────────────────────
  async getContasCorrentesCombo(): Promise<ContaCorrenteCombo[]> {
    const { data } = await apiClient.get<ContaCorrenteCombo[]>('/api/contas-correntes');
    return data;
  },

  async listarContasCorrentes(params?: { busca?: string; apenasAtivas?: boolean; pagina?: number; tamanhoPagina?: number }): Promise<PaginadoResponse<ContaCorrente>> {
    const { data } = await apiClient.get<PaginadoResponse<ContaCorrente>>('/api/cadastros/contas-correntes', { params });
    return data;
  },

  async criarContaCorrente(req: SalvarContaCorrenteRequest): Promise<ContaCorrente> {
    const { data } = await apiClient.post<ContaCorrente>('/api/cadastros/contas-correntes', req);
    return data;
  },

  async atualizarContaCorrente(id: number, req: SalvarContaCorrenteRequest): Promise<void> {
    await apiClient.put(`/api/cadastros/contas-correntes/${id}`, req);
  },

  async inativarContaCorrente(id: number): Promise<void> {
    await apiClient.delete(`/api/cadastros/contas-correntes/${id}`);
  },

  // ── Integração API (Inter / ACBr) ───────────────────────────────────────

  async getIntegracaoContaCorrente(id: number): Promise<import('../types').ContaCorrenteIntegracao> {
    const { data } = await apiClient.get<import('../types').ContaCorrenteIntegracao>(
      `/api/cadastros/contas-correntes/${id}/integracao`);
    return data;
  },

  async salvarIntegracaoContaCorrente(
    id: number, req: import('../types').ContaCorrenteIntegracao,
  ): Promise<void> {
    await apiClient.put(`/api/cadastros/contas-correntes/${id}/integracao`, req);
  },

  async uploadCertificadoContaCorrente(
    id: number, arquivo: File, tipo: 'CRT' | 'KEY' | 'PFX', senha?: string,
  ): Promise<import('../types').UploadCertificadoResultado> {
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    fd.append('tipo', tipo);
    // Só para PFX: a API valida e guarda cifrada; nunca a devolve.
    if (senha) fd.append('senha', senha);
    const { data } = await apiClient.post<import('../types').UploadCertificadoResultado>(
      `/api/cadastros/contas-correntes/${id}/integracao/upload-certificado`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  // ── Cartões ──────────────────────────────────────────────────────────────
  async getCartoesCombo(): Promise<CartaoCombo[]> {
    const { data } = await apiClient.get<CartaoCombo[]>('/api/cartoes');
    return data;
  },

  async listarCartoes(params?: { busca?: string; apenasAtivos?: boolean; pagina?: number; tamanhoPagina?: number }): Promise<PaginadoResponse<Cartao>> {
    const { data } = await apiClient.get<PaginadoResponse<Cartao>>('/api/cadastros/cartoes', { params });
    return data;
  },

  async criarCartao(req: SalvarCartaoRequest): Promise<Cartao> {
    const { data } = await apiClient.post<Cartao>('/api/cadastros/cartoes', req);
    return data;
  },

  async atualizarCartao(id: number, req: SalvarCartaoRequest): Promise<void> {
    await apiClient.put(`/api/cadastros/cartoes/${id}`, req);
  },

  async inativarCartao(id: number): Promise<void> {
    await apiClient.delete(`/api/cadastros/cartoes/${id}`);
  },

  // ── Formas de Pagamento ──────────────────────────────────────────────────
  async getFormasPagamentoCombo(vendaPrazo?: boolean): Promise<FormaPagamentoCombo[]> {
    const { data } = await apiClient.get<FormaPagamentoCombo[]>('/api/formas-pagamento', {
      params: vendaPrazo !== undefined ? { vendaPrazo } : undefined,
    });
    return data;
  },

  async listarFormasPagamento(params?: { busca?: string; apenasAtivos?: boolean; pagina?: number; tamanhoPagina?: number }): Promise<PaginadoResponse<FormaPagamento>> {
    const { data } = await apiClient.get<PaginadoResponse<FormaPagamento>>('/api/cadastros/formas-pagamento', { params });
    return data;
  },

  async getFormaPagamentoById(id: number): Promise<FormaPagamento> {
    const { data } = await apiClient.get<FormaPagamento>(`/api/cadastros/formas-pagamento/${id}`);
    return data;
  },

  async criarFormaPagamento(req: SalvarFormaPagamentoRequest): Promise<FormaPagamento> {
    const { data } = await apiClient.post<FormaPagamento>('/api/cadastros/formas-pagamento', req);
    return data;
  },

  async atualizarFormaPagamento(id: number, req: SalvarFormaPagamentoRequest): Promise<void> {
    await apiClient.put(`/api/cadastros/formas-pagamento/${id}`, req);
  },

  async inativarFormaPagamento(id: number): Promise<void> {
    await apiClient.delete(`/api/cadastros/formas-pagamento/${id}`);
  },

  // ── Condições de Pagamento ───────────────────────────────────────────────
  async getCondicoesPagamentoCombo(formaPagRecId?: number): Promise<CondicaoPagamentoCombo[]> {
    const { data } = await apiClient.get<CondicaoPagamentoCombo[]>('/api/condicoes-pagamento', {
      params: formaPagRecId ? { formaPagRecId } : undefined,
    });
    return data;
  },

  async listarCondicoesPagamento(params?: { formaPagRecId?: number; busca?: string; pagina?: number; tamanhoPagina?: number }): Promise<PaginadoResponse<CondicaoPagamento>> {
    const { data } = await apiClient.get<PaginadoResponse<CondicaoPagamento>>('/api/cadastros/condicoes-pagamento', { params });
    return data;
  },

  async getCondicaoPagamentoById(id: number): Promise<CondicaoPagamentoDetalhe> {
    const { data } = await apiClient.get<CondicaoPagamentoDetalhe>(`/api/cadastros/condicoes-pagamento/${id}`);
    return data;
  },

  async criarCondicaoPagamento(req: SalvarCondicaoPagamentoRequest): Promise<CondicaoPagamento> {
    const { data } = await apiClient.post<CondicaoPagamento>('/api/cadastros/condicoes-pagamento', req);
    return data;
  },

  async atualizarCondicaoPagamento(id: number, req: SalvarCondicaoPagamentoRequest): Promise<void> {
    await apiClient.put(`/api/cadastros/condicoes-pagamento/${id}`, req);
  },

  async bloquearCondicaoPagamento(id: number): Promise<void> {
    await apiClient.delete(`/api/cadastros/condicoes-pagamento/${id}`);
  },
};
